/**
 * Competition questions: what a member is asked about a competition, served by
 * the API's CompetitionQuestions registry (GET/PUT competitionQuestions/{type}/{id}).
 *
 * The client knows NO question. It draws whatever list the API returns, as
 * ticks, and sends back one answer per question: ticked is yes, unticked is no.
 * So a new question (say, sharing contact details with a competition's
 * players) reaches the website with no release. Mirrored by the app's
 * competition_questions.dart.
 *
 * Each question is ONE compact row (owner, 25 Sep 2026): a short `label`
 * beside a switch that starts off, and `details` (a few short lines) behind
 * "More info".
 *
 * Pure helpers; the network side is in the components. Imported explicitly
 * (house style), never auto-imported.
 */
import {ref} from 'vue';

/** A join link's referenceType, as the competition type the questions endpoint takes. Clubs and players are not competitions. */
const TYPE_BY_INVITE_REFERENCE = {1: 'ladder', 2: 'leagueCycle', 4: 'tournament', 5: 'meet', 6: 'americanoCycle'};

export function competitionTypeForInvite(referenceType) {
  return TYPE_BY_INVITE_REFERENCE[referenceType] ?? null;
}

/** One answer per question: ticked is yes, anything else is no. */
export function competitionAnswers(questions, ticked) {
  return (questions ?? []).map((q) => ({key: q.key, answer: ticked?.[q.key] === true}));
}

/** Closing the popup without saving is a no to every question on it (owner decision, 21 Sep 2026). */
export function competitionAnswersOnClose(questions) {
  return competitionAnswers(questions, {});
}

/** The pending response's list, whatever shape an older or failing API hands back. */
export function pendingQuestions(response) {
  return Array.isArray(response?.questions) ? response.questions : [];
}

/** One key per member and competition, so a session asks once however often the page is opened. */
export function competitionQuestionsKey(userId, type, id) {
  return `${userId ?? ''}|${type ?? ''}|${String(id ?? '').toLowerCase()}`;
}

/**
 * Competitions whose questions were already put to the member in this browser
 * session (client only; the prompt never adds to it during a server render).
 * Stops the dialog coming back while an answer is still on its way; the API
 * stops returning a question once it is answered.
 */
export const competitionQuestionsAsked = new Set();

/**
 * The join confirmation's questions (/invite/[slug]), answered as part of
 * joining.
 *
 * Joining replaces the stored user, and the page reloads its questions when
 * the member changes. That reload used to empty the list between the join and
 * the save, so the save had nothing to send and the answer was lost, ticked or
 * not (tester finding, 25 Sep 2026). So: capture the answers BEFORE joining
 * (`beginJoin`), save that capture whatever happens to the list afterwards
 * (`saveAnswers`), and refuse to reload while a join is in flight (`endJoin`
 * lifts it). A failed save never blocks the join: the competition page asks
 * what did not save.
 *
 * `fetchPending()` returns the questions waiting (or [] for none);
 * `putAnswers(answers)` records them.
 */
export function createJoinQuestions({fetchPending, putAnswers}) {
  const questions = ref([]);
  const ticks = ref({});
  let joining = false;

  const load = async () => {
    if (joining) return;
    questions.value = [];
    try {
      const list = await fetchPending();
      if (!joining) questions.value = Array.isArray(list) ? list : [];
    } catch (e) {
      questions.value = []; // the competition page asks instead
    }
  };

  const beginJoin = () => {
    joining = true;
    return competitionAnswers(questions.value, ticks.value);
  };

  const saveAnswers = async (captured) => {
    if (!captured?.length) return;
    try {
      await putAnswers(captured);
    } catch (e) {
      // Never block a join on this.
    }
  };

  const endJoin = () => {
    joining = false;
  };

  return {questions, ticks, load, beginJoin, saveAnswers, endJoin};
}
